import { environment } from '@rapider/environments';

function isLoopbackHostname(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
}

/**
 * Returns true ONLY when running local development on a loopback host (localhost / 127.0.0.1)
 * with production === false. Evaluates to false on deployed dev (dev.app.rapider.ai) and
 * prod (app.rapider.ai) environments so deployed iframes can never load or probe localhost.
 */
export function isLocalDevelopmentHost(): boolean {
  if (environment?.production) {
    return false;
  }
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false;
  }
  return isLoopbackHostname(window.location.hostname);
}

/**
 * Validates whether a manifest URL, page HTML URL, or asset base URL belongs to a
 * trusted Rapider origin (same-origin host, configured API origin, DigitalOcean Spaces,
 * or localhost:* strictly during local development).
 * Rejects untrusted third-party domains, non-HTTPS external protocols, and path traversal.
 */
export function isTrustedAppResourceUrl(rawUrl: string | null | undefined): boolean {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return false;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return false;
  }

  // Reject dangerous schemes and protocol-relative external URLs
  if (
    /^(javascript|data|vbscript|file|blob):/i.test(trimmed) ||
    trimmed.startsWith('//')
  ) {
    return false;
  }

  // Allow clean root-relative paths on the active host
  if (trimmed.startsWith('/')) {
    return !trimmed.includes('..');
  }

  const baseOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost:4200';

  let parsed: URL;
  try {
    parsed = new URL(trimmed, baseOrigin);
  } catch {
    return false;
  }

  if (decodeURIComponent(parsed.pathname).includes('..')) {
    return false;
  }

  // 1. Allow active UI host origin (e.g. app.rapider.ai, dev.app.rapider.ai, localhost:4200)
  if (parsed.origin === baseOrigin) {
    return true;
  }

  // 2. Allow any localhost / 127.0.0.1 port (e.g. :3300 harness, :8081 local API, :9000 local S3)
  // ONLY when running local development — strictly blocked on dev/prod deployments!
  if (
    isLocalDevelopmentHost() &&
    (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
    isLoopbackHostname(parsed.hostname)
  ) {
    return true;
  }

  // 3. Allow configured backend API origin if applicable (excluding localhost on non-local deployments)
  if (environment?.apiBaseUrl) {
    try {
      const apiUrl = new URL(environment.apiBaseUrl, baseOrigin);
      if (
        parsed.origin === apiUrl.origin &&
        (!isLoopbackHostname(apiUrl.hostname) || isLocalDevelopmentHost())
      ) {
        return true;
      }
    } catch {
      // Ignore malformed apiBaseUrl
    }
  }

  // 4. External storage origins MUST use HTTPS and match DigitalOcean Spaces
  if (parsed.protocol !== 'https:') {
    return false;
  }

  const host = parsed.hostname.toLowerCase();
  const isDigitalOceanSpaces =
    host === 'digitaloceanspaces.com' ||
    host.endsWith('.digitaloceanspaces.com');

  return isDigitalOceanSpaces;
}

/**
 * Generates a strict Content-Security-Policy <meta> tag for microapp iframe sandboxes.
 * Allows scripts/styles/assets only from the host origin, DigitalOcean Spaces, Rapider's
 * pinned UI CDNs, and (strictly during local development) localhost:* / 127.0.0.1:*.
 */
export function buildSandboxCspMetaTag(hostOrigin: string): string {
  const safeHost = hostOrigin.replace(/["'<>]/g, '');
  const isLocalDev = isLocalDevelopmentHost();
  const localHttpSources = isLocalDev
    ? ' http://localhost:* http://127.0.0.1:* https://localhost:* https://127.0.0.1:*'
    : '';
  const localWsSources = isLocalDev
    ? ' ws://localhost:* ws://127.0.0.1:* wss://localhost:* wss://127.0.0.1:*'
    : '';
  const doSpaces = 'https://*.digitaloceanspaces.com';
  const jsdelivr = 'https://cdn.jsdelivr.net';
  const faSources =
    'https://kit.fontawesome.com https://ka-f.fontawesome.com https://ka-p.fontawesome.com';
  const gFontsStyle = 'https://fonts.googleapis.com';
  const gFontsFont = 'https://fonts.gstatic.com';

  const directives = [
    `default-src 'self' blob: data: ${safeHost}${localHttpSources} ${doSpaces}`,
    `script-src 'unsafe-inline' blob: ${safeHost}${localHttpSources} ${doSpaces} ${jsdelivr} ${faSources}`,
    `style-src 'unsafe-inline' ${safeHost}${localHttpSources} ${doSpaces} ${jsdelivr} ${gFontsStyle} ${faSources}`,
    `font-src 'self' data: ${safeHost}${localHttpSources} ${doSpaces} ${gFontsFont} ${jsdelivr} ${faSources}`,
    `img-src https:${localHttpSources} data: blob:`,
    `media-src ${safeHost}${localHttpSources} ${doSpaces} data: blob:`,
    `connect-src ${safeHost}${localHttpSources}${localWsSources} ${doSpaces} ${jsdelivr} ${faSources}`,
    `worker-src 'self' blob:`,
    `frame-src 'none'`,
    `object-src 'none'`,
    `base-uri ${safeHost}${localHttpSources} ${doSpaces}`,
  ];

  return `<meta data-rapider-injected="true" http-equiv="Content-Security-Policy" content="${directives.join('; ')}">`;
}

/**
 * Verifies that a requested entityName in window.rapiderApi is declared in the
 * microapplication's manifest dataModels (when dataModels are defined).
 */
export function isAllowedManifestEntity(
  entityName: string,
  dataModels: any[] | null | undefined,
): boolean {
  if (!entityName || typeof entityName !== 'string') {
    return false;
  }

  // Reject path traversal or query injection characters in entityName
  if (!/^[A-Za-z0-9_-]+$/.test(entityName)) {
    return false;
  }

  if (!Array.isArray(dataModels) || dataModels.length === 0) {
    return true;
  }

  const target = entityName.trim().toLowerCase();
  return dataModels.some((model: any) => {
    if (!model) return false;
    const candidates = [
      model.name,
      model.datatableName,
      model.dataTableName,
      model.key,
    ];
    return candidates.some(
      (c) => typeof c === 'string' && c.trim().toLowerCase() === target,
    );
  });
}
